import { apiClient, apiFetchWithMeta } from '@/lib/api-client';
import type {
  BulkActionResult,
  Server,
  ServerGroup,
  ServerInventory,
  ServerStatus,
  SshCredential,
} from '@/types/api';

export interface ServerFilters {
  status?: ServerStatus | '';
  groupId?: string;
  tag?: string;
  search?: string;
  cursor?: string;
  /** Up to 100; the API's default is 25. */
  limit?: number;
}

/**
 * The optional members are `?: T | undefined` rather than plain `?: T` because the form
 * builds one object literal from fields the user may have left blank, so the keys are
 * always present and sometimes undefined. exactOptionalPropertyTypes treats that as
 * distinct from omitting them; JSON.stringify drops undefined either way.
 */
export interface ServerPayload {
  ssh_host: string;
  ssh_port: number;
  ssh_user: string;
  /** Optional — defaults to the address. */
  name?: string | undefined;
  /** Either reference a saved credential… */
  credential_id?: string | null | undefined;
  /** …or supply the secret inline and let the server store it. */
  credential_secret?: string | undefined;
  credential_auth_type?: 'private_key' | 'password' | undefined;
  credential_passphrase?: string | undefined;
  /** The sudo password for an inline credential whose sudo asks for one. Never returned. */
  credential_sudo_password?: string | undefined;
  group_id?: string | null | undefined;
  tags?: string[] | undefined;
  notes?: string | undefined;
}

export const serversApi = {
  list: (filters: ServerFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.groupId) params.set('group_id', filters.groupId);
    if (filters.tag) params.set('tag', filters.tag);
    if (filters.search) params.set('search', filters.search);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit) params.set('limit', String(filters.limit));

    const query = params.toString();
    return apiFetchWithMeta<Server[]>(`/servers${query ? `?${query}` : ''}`);
  },

  /**
   * Every server on the account, following the pages to the end.
   *
   * For pickers, where a server missing from the list because it was on page two is a server that
   * cannot be chosen. Bounded, so a runaway cursor cannot loop forever.
   */
  all: async (): Promise<Server[]> => {
    const servers: Server[] = [];
    let cursor: string | undefined;

    for (let page = 0; page < 50; page += 1) {
      const result = await serversApi.list({ limit: 100, ...(cursor ? { cursor } : {}) });
      servers.push(...result.data);
      const next = result.meta.next_cursor;

      if (typeof next !== 'string' || next === '') {
        break;
      }

      cursor = next;
    }

    return servers;
  },

  inventory: () => apiClient.get<ServerInventory>('/servers/inventory'),

  create: (payload: ServerPayload) => apiClient.post<Server>('/servers', payload),
  update: (id: string, payload: ServerPayload) => apiClient.patch<Server>(`/servers/${id}`, payload),
  remove: (id: string) => apiClient.delete<void>(`/servers/${id}`),

  /**
   * What deleting this server would also remove from the platform.
   *
   * Read when the confirmation opens. Deleting a server takes what the platform records about it
   * with it — monitors, backup sources and their schedules — and asking somebody to agree to that is
   * only fair if they can see the count first.
   */
  dependents: (id: string) =>
    apiClient.get<{ items: { label: string; count: number }[] }>(`/servers/${id}/dependents`),

  /** Connects, authenticates, and detects the OS. Slow by nature — it is a live SSH call. */
  test: (id: string) => apiClient.post<Server>(`/servers/${id}/test`),
  /** Forgets the recorded host key and connects again; only after a check met a different key. */
  trustNewHostKey: (id: string) => apiClient.post<Server>(`/servers/${id}/trust-new-host-key`),

  bulk: (body: {
    action: 'test_connection' | 'assign_group' | 'add_tags' | 'remove_tags' | 'delete';
    server_ids: string[];
    group_id?: string | null;
    tags?: string[];
  }) => apiClient.post<BulkActionResult>('/servers/bulk', body),

  createCredential: (body: CredentialPayload) =>
    apiClient.post<SshCredential>('/servers/credentials', body),
  updateCredential: (id: string, body: CredentialPayload) =>
    apiClient.patch<SshCredential>(`/servers/credentials/${id}`, body),
  removeCredential: (id: string) => apiClient.delete<void>(`/servers/credentials/${id}`),

  createGroup: (body: GroupPayload) => apiClient.post<ServerGroup>('/servers/groups', body),
  updateGroup: (id: string, body: GroupPayload) =>
    apiClient.patch<ServerGroup>(`/servers/groups/${id}`, body),
  removeGroup: (id: string) => apiClient.delete<void>(`/servers/groups/${id}`),
};

export interface CredentialPayload {
  name: string;
  auth_type: 'private_key' | 'password';
  username: string;
  /** Empty on update means "keep the existing secret". */
  secret: string;
  passphrase?: string;
  /**
   * The account's sudo password, when its sudo asks for one. Write-only: the API never returns it.
   * Empty on update keeps whatever is stored.
   */
  sudo_password?: string;
  /** Removes a stored sudo password. Ignored when a new one is given. */
  forget_sudo_password?: boolean;
}

export interface GroupPayload {
  name: string;
  description?: string;
  colour?: string;
}
